/** Normalize / pick SIP WebSocket URLs for browser softphone (JsSIP). */

export function isPrivateLanHost(host: string): boolean {
  const h = String(host || "")
    .trim()
    .replace(/^https?:\/\//i, "")
    .split("/")[0]
    .split(":")[0]
    .toLowerCase();
  if (!h) return false;
  if (h === "localhost" || h === "127.0.0.1") return true;
  if (/^192\.168\.\d{1,3}\.\d{1,3}$/.test(h)) return true;
  if (/^10\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(h)) return true;
  if (/^172\.(1[6-9]|2\d|3[0-1])\.\d{1,3}\.\d{1,3}$/.test(h)) return true;
  return false;
}

export function normalizeSipWsUrl(raw: string, host?: string): string {
  let u = String(raw || "").trim();
  const h = String(host || "")
    .trim()
    .replace(/^https?:\/\//i, "")
    .replace(/\/.*$/, "")
    .replace(/:\d+$/, "");

  // Truncation fix: ...:8089/w → ...:8089/ws
  if (/\/w$/i.test(u) && !/\/ws$/i.test(u)) {
    u = `${u}s`;
  }

  if (!u && h) {
    if (typeof window !== "undefined" && window.location.protocol === "https:") {
      // HTTPS site + LAN PBX still needs a reachable wss; prefer LAN ws only when page is http
      return isPrivateLanHost(h) ? `wss://${h}:8089/ws` : `wss://${h}:8089/ws`;
    }
    return `ws://${h}:8088/ws`;
  }
  if (!u) return "";

  if (!/^wss?:\/\//i.test(u)) {
    const secure =
      typeof window !== "undefined" && window.location.protocol === "https:";
    u = `${secure ? "wss" : "ws"}://${u.replace(/^\/\//, "")}`;
  }

  try {
    const parsed = new URL(u);
    if (!parsed.pathname || parsed.pathname === "/") {
      parsed.pathname = "/ws";
      u = parsed.toString().replace(/\/$/, "");
      if (!/\/ws$/i.test(u)) u = u.replace(/\/?$/, "") + "/ws";
    }
  } catch {
    /* keep */
  }

  if (
    typeof window !== "undefined" &&
    window.location.protocol === "https:" &&
    u.toLowerCase().startsWith("ws://")
  ) {
    u = "wss://" + u.slice(5);
    u = u.replace(":8088/", ":8089/");
  }

  return u;
}

/**
 * Best WebSocket for this browser context.
 * On LAN (http://192.168.x.x) always use direct PBX ws://IP:8088/ws —
 * public wss://ipbx… often fails from office LAN.
 */
export function resolveSipWsForBrowser(host: string, savedWs?: string): string {
  const h = String(host || "")
    .trim()
    .replace(/^https?:\/\//i, "")
    .split("/")[0]
    .replace(/:\d+$/, "");
  if (!h) return normalizeSipWsUrl(savedWs || "", "");

  const pageHttp =
    typeof window !== "undefined" && window.location.protocol === "http:";
  const pageHost =
    typeof window !== "undefined" ? window.location.hostname : "";

  if (isPrivateLanHost(h) && (pageHttp || isPrivateLanHost(pageHost))) {
    return `ws://${h}:8088/ws`;
  }

  return normalizeSipWsUrl(savedWs || "", h);
}

/** Ordered candidates to try until REGISTER succeeds. */
export function sipWsCandidates(host: string, savedWs?: string): string[] {
  const h = String(host || "")
    .trim()
    .replace(/^https?:\/\//i, "")
    .split("/")[0]
    .replace(/:\d+$/, "");
  const primary = resolveSipWsForBrowser(h, savedWs);
  const saved = normalizeSipWsUrl(savedWs || "", h);
  const list = [
    primary,
    h ? `ws://${h}:8088/ws` : "",
    h ? `ws://${h}:8088/` : "",
    saved,
    h ? `wss://${h}:8089/ws` : "",
  ];
  return [...new Set(list.map((x) => String(x || "").trim()).filter(Boolean))];
}

export function defaultSipWsForHost(host: string): string {
  return resolveSipWsForBrowser(host, "");
}
